import {FaqModel} from '@/models/faq.model';
import {
  ChallengeModel,
  ChallengeProblemLocation,
  VisitorsResponse,
  ChallengeSituation,
  ChallengePrizes
} from '@/models/challenge.model';
import {Observable} from 'rxjs';
import {map, retry} from 'rxjs/operators';
import {JudgeModel} from '@/models/judge.model';
import {AdviserModel} from '@/models/adviserModel';
import {BaseApi, PaginatedParams, PaginatedResponse, RequireAuthApi} from '@/api/base.api';
import {axiosInstance, axiosInstanceAuthenticated} from '@/api/axios.config.api';
import {UserModel} from '@/models/user.model';
import {GalleryModel} from '@/models/gallery.model';
import {ChallengeFileModel} from '@/models/challenge-file.model';
import {PartnerModel} from '@/models/partner.model';

export class ChallengesApi extends BaseApi<ChallengeModel> {
  constructor() {
    super('challenges', [
      RequireAuthApi.CREATE,
      RequireAuthApi.UPDATE,
      RequireAuthApi.DESTROY,
    ])
  }

  all(params?: PaginatedParams): Observable<ChallengeModel[]> {
    return axiosInstance().get<PaginatedResponse<ChallengeModel>>(this.url, {params})
      .pipe(map((response) => response.data.data))
  }

  findBySlug(slug: string): Observable<ChallengeModel> {
    return axiosInstance().get<ChallengeModel>(`${this.url}/slug/${slug}`)
      .pipe(retry(1), map((response) => response.data))
  }

  fetchHomeActiveChallenges(): Observable<ChallengeModel[]> {
    return axiosInstance().get<ChallengeModel[]>('settings/active-challenges')
      .pipe(map((response) => response.data))
  }

  fetchHomeFinishedChallenges(): Observable<ChallengeModel[]> {
    return axiosInstance().get<ChallengeModel[]>('settings/finished-challenges')
      .pipe(map((response) => response.data))
  }

  getChallengesbyFilter(status: ChallengeSituation,
                        page: number = 1,
                        tag?: string)
    : Observable<PaginatedResponse<ChallengeModel>> {
    const params: PaginatedParams = {
      'filter[situation]': status,
      page
    };
    if (!!tag) {
      params['filter[tag]'] = tag;
    }
    return axiosInstance().get<PaginatedResponse<ChallengeModel>>(this.url, {params})
      .pipe(map((response) =>  response.data))
  }

  registerVisitor(id: number) {
    return axiosInstance().post(`${this.url}/${id}/visitors`)
      .pipe(map((response) => response.data));
  }

  getVisitors(id: number): Observable<VisitorsResponse> {
    return axiosInstance().get(`${this.url}/${id}/visitors`)
      .pipe(map((response) => response.data));
  }

  searchByTag(query: string): Observable<ChallengeModel[]> {
    const params = ({search: query.trim().toLowerCase()});
    return axiosInstance().get(`${this.url}/tags`, {params})
      .pipe(map((response) => response.data));
  }

  getFaqs(challengerId: number): Observable<FaqModel[]> {
    return axiosInstance().get<FaqModel[]>(`${this.url}/${challengerId}/faqs`)
      .pipe(map((response) => response.data));
  }

  getGallery(challengeId: number, params: PaginatedParams): Observable<PaginatedResponse<GalleryModel>> {
    return axiosInstance().get<PaginatedResponse<GalleryModel>>(`${this.url}/${challengeId}/gallery`, {params})
      .pipe(map((response) => response.data));
  }

  getFiles(challengeId: number, params: PaginatedParams): Observable<PaginatedResponse<ChallengeFileModel>> {
    return axiosInstance().get<PaginatedResponse<ChallengeFileModel>>(`${this.url}/${challengeId}/files`, {params})
      .pipe(map((response) => response.data));
  }

  findJudges(challengeId: number): Observable<JudgeModel[]> {
    return axiosInstance().get<JudgeModel[]>(`${this.url}/${challengeId}/judges`)
      .pipe(map((response) => response.data));
  }

  findPartners(challengeId: number): Observable<PartnerModel[]> {
    return axiosInstance().get<PartnerModel[]>(`${this.url}/${challengeId}/partners`)
      .pipe(map((response) => response.data));
  }

  findAdvisers(challengeId: number): Observable<AdviserModel[]> {
    return axiosInstance().get<AdviserModel[]>(`${this.url}/${challengeId}/advisors`)
      .pipe(map((response) => response.data));
  }

  getLocalesWithSameProblem(challengeId: number): Observable<ChallengeProblemLocation[]> {
    return axiosInstance().get<ChallengeProblemLocation[]>(`${this.url}/${challengeId}/problem-locations`)
      .pipe(map((response) => response.data));
  }

  findCommunity(challengeId: number): Observable<UserModel[]> {
    return axiosInstance().get<UserModel[]>(`${this.url}/${challengeId}/community`)
      .pipe(map((response) => response.data))
  }

  fetchPrizes(challengeId: number): Observable<ChallengePrizes[]> {
    return axiosInstance().get<ChallengePrizes[]>(`${this.url}/${challengeId}/prizes`)
      .pipe(map((response) => response.data))
  }
}
